import debug from "debug";

import { type Config, type Exact, getConfig, isServerSide } from "@jti/fe-common";
import { getMessageBus } from "@jti/fe-message-bus";
import { BrowserStorage, getUser as getUserData, LoyaltyApi, ReferralApi, User } from "@jti/rest-api";

import type { StandardData } from "../../events";
import { GoogleTagManagerEvents } from "../../events";
import { addTrackingEventListener } from "../../helpers";
import type { TrackingEvent } from "../../trackingEvents/TrackingEvent";
import type { NonCommerceEventsKeys, TrackingGTMCommerceEventsMap } from "../../trackingEvents/types";
import { TrackingEvents } from "../../trackingEvents/types";

const log = debug("jti-aem:gtm");
const logCommerce = debug("jti-aem:gtm:commerce");

let instance: BaseGTMRunner | null = null;

export class BaseGTMRunner {
  points: number;
  tier: string;
  referee: string;
  currentName?: string;
  promiseTimeoutInMs: number;
  config: Config;
  excludedRoutes: string[];
  messageBus: ReturnType<typeof getMessageBus>;
  instance: GoogleTagManagerEvents | null = null;
  isCartPage: boolean = false;
  isLoyaltyPage: boolean = false;
  gtmContainer: HTMLElement | null = null;
  onLoadTracking?: string;
  protected customEventsRegistered = false;
  protected messageBusEventsRegistered = false;

  constructor() {
    /**
     * @type {number}
     */
    this.points = 0;
    this.tier = "";
    this.referee = "";
    this.currentName = "";
    this.promiseTimeoutInMs = 15000;
    this.config = getConfig();
    this.excludedRoutes = ["", "#/"];
    this.messageBus = getMessageBus();

    if (!isServerSide()) {
      let currentHash = window?.location.hash;
      window.addEventListener("hashchange", () => {
        if (!this.isLoyaltyPage && !this.isCartPage && currentHash !== window.location.hash) {
          currentHash = window.location.hash;

          if (!this.excludedRoutes.includes(currentHash)) {
            this.handleHashChange();
          }
        }
      });
    }
  }

  handleHashChange() {
    if (this.gtmContainer) {
      this.currentName = this.getCurrentNameForGtm();
      this.instance = null;
      this.getCurrentDataObject();
    }
  }

  getCurrentNameForGtm() {
    if (isServerSide()) {
      return;
    }
    /*
     * TODO: think about automated solution
     * */
    const pageName = this.gtmContainer?.dataset.pageName;
    const hash = window.location.hash;

    if (hash.length && !this.isLoyaltyPage) {
      const reactAppRoute = hash.replace("#/", "");

      const reactAppRoutes = {
        AGE_VERIFICATION: /^age-verification$/g,
        ORDERS: /^orders.*$/g,
        ACCOUNT: /^account$/g,
        ADDRESS: /^address.*$/g,
        PAYMENTS: /^payments$/g,
        DEVICES: /^devices$/g,
        COMMUNICATION: /^communication$/g,
        CHANGE_PASSWORD: /^change-password$/g,
        UPDATE_PHONE: /^update-phone$/g,
        UPDATE_PERSONAL_DETAILS: /^update-personal-details$/g,
        CHECKOUT: /^checkout$/g,
        FREETRIAL: /^trial\/checkout.*$/g,
        LOGIN: /^login$/g,
        REGISTER: /^register(?:\/\d+)?(?:\?.*)?$/g,
        MY_SUBSCRIPTION: /^subscription$/g,
        ENTRY: /^entry$/g,
      };

      const gtmEventMap = {
        ROOT: this.isCartPage ? "cart" : "account-page",
        ACCOUNT: "account-information",
        ADDRESS: "my-addresses",
        CHECKOUT: "checkout",
        REGISTER: "signup_regular",
        COMMUNICATION: "communication-preferences",
        LOGIN: "login-page",
        ORDERS: "my-orders",
        FREETRIAL: "free-trial-checkout",
        DEVICES: "my-devices",
        MY_SUBSCRIPTION: "my-subscription",
        ENTRY: "login-page",
      };

      const [keyFromRoute] = Object.entries(reactAppRoutes).find(([, regexp]) => regexp.test(reactAppRoute)) || [
        "ROOT",
      ];
      return gtmEventMap[keyFromRoute as keyof typeof gtmEventMap] || pageName;
    }

    return pageName;
  }

  /**
   * Ensures window.dataLayer is initialized as an array.
   * The GTM inline script may use a deferred loading strategy (e.g. "lazyOnload")
   * which delays window.dataLayer creation until after all page resources load.
   * Tracking events can fire before that, so we must ensure the array exists.
   * This follows the standard GTM integration pattern — the GTM snippet's own
   * `w[l] = w[l] || []` will safely no-op if the array already exists.
   */
  private ensureDataLayer(): void {
    if (!isServerSide()) {
      window.dataLayer = window.dataLayer || [];
    }
  }

  getStandardData(name: string | undefined, callback?: (data: StandardData) => void) {
    if (!name || !this.instance) {
      return null;
    }
    const data = this.instance.getStandardData(name);
    callback?.(data);
    return data;
  }

  async trackActiveInstanceEvent(
    eventName: string,
    additionalData: NonNullable<TrackingEvent<TrackingEvents>["detail"]>["additionalData"] | object = {}
  ) {
    try {
      await this.waitForInstance(this.promiseTimeoutInMs);
      const data = this.getStandardData(eventName);

      if (!data) {
        return;
      }

      this.ensureDataLayer();
      log(`Tracking event ${eventName}${additionalData ? ` with data: ${JSON.stringify(additionalData)}` : ""}`);

      window.dataLayer.push({
        ...data,
        ...additionalData,
      });
    } catch (e) {
      console.error(e);
    }
  }

  trackMessageBusEvents() {
    if (this.messageBusEventsRegistered) return;

    this.messageBusEventsRegistered = true;
    this.messageBus.subscribe({
      channel: "tracking.event",
      callback: ({ payload }) => {
        if (payload?.event_name) {
          log(
            `MessageBus tracking event cought ${payload.event_name}${
              payload.additionalData ? ` with data: ${JSON.stringify(payload.additionalData)}` : ""
            }`
          );

          this.trackActiveInstanceEvent(payload.event_name, payload.additionalData);
        }
      },
    });
  }

  runOnLoadTracking() {
    if (this.onLoadTracking && !this.isCartPage) {
      this.trackEvent(this.onLoadTracking, undefined);
    }
  }

  async getCurrentDataObject() {
    if (!this.instance) {
      try {
        const userData = getUserData();
        if (userData.uuid) {
          if (this.config.IS_LOYALTY_ENABLED === "true") {
            const loyaltyApi = LoyaltyApi.init();
            const loyaltyStatus = await loyaltyApi.getStatus();
            if (loyaltyStatus) {
              const loyaltySummary = await loyaltyApi.getLoyaltySummary(userData.uuid, userData.ciam_token, true);
              this.points = loyaltySummary?.points?.points ?? 0;
              this.tier = loyaltySummary?.tier?.name || "";
              new User(new BrowserStorage()).setUser({ loyalty: { points: this.points, tier: this.tier } });
            }
          }
          const referralApi = ReferralApi.init();
          const isActive = await referralApi.getStatus();
          if (isActive) {
            const rasCustomer = await referralApi.getCustomer(userData.uuid, userData.ciam_token);
            if (rasCustomer?.code) {
              this.referee = rasCustomer?.parent_code ? "yes" : "no";
            }
          }
        }
      } finally {
        this.createInstance();
      }
    }

    return this.instance;
  }

  createInstance() {
    this.instance = new GoogleTagManagerEvents(
      this.currentName || "other",
      () => {
        if (!isServerSide()) {
          sessionStorage.setItem("gtmInstance", "true");
          this?.runOnLoadTracking();
        }
      },
      this.points,
      this.referee,
      this.tier
    );
  }

  async trackEvent(
    name: string,
    additionalData: NonNullable<TrackingEvent<NonCommerceEventsKeys>["detail"]>["additionalData"]
  ) {
    try {
      await this.waitForInstance(this.promiseTimeoutInMs);

      const data = this.getStandardData(name);

      if (!data) {
        return;
      }

      const eventInfo = {
        ...data,
        ...additionalData,
      };

      this.ensureDataLayer();
      log(`Tracking event ${name}${eventInfo ? ` with data: ${JSON.stringify(eventInfo)}` : ""}`);
      window.dataLayer.push(eventInfo);
    } catch (e) {
      log(`Error tracking event ${name}`);
      console.error(e);
    }
  }

  waitForInstance(timeout: number): Promise<GoogleTagManagerEvents> {
    // ONLY resolve when instance is ready - meaning it's got all data required by the GTM tracking
    if (this.instance?.isReady) {
      return Promise.resolve(this.instance);
    }

    return new Promise((resolve, reject) => {
      const interval = setInterval(() => {
        if (this.instance?.isReady) {
          resolve(this.instance);
          clearInterval(interval);
        }
      }, 500);

      setTimeout(() => {
        reject(new Error("GTM instance is unavailable"));
        clearInterval(interval);
      }, timeout);
    });
  }

  async trackCommerceEvent<Key extends keyof TrackingGTMCommerceEventsMap, T>(
    name: Key,
    additionalData?: Exact<T, TrackingGTMCommerceEventsMap[Key]>
  ) {
    try {
      await this.waitForInstance(this.promiseTimeoutInMs);

      const data = this.getStandardData(name);

      if (!data) {
        return;
      }
      const eventInfo = {
        ...data,
        ...additionalData,
      };

      logCommerce(`Tracking event ${name}${additionalData ? ` with data: ${JSON.stringify(additionalData)}` : ""}`);

      this.ensureDataLayer();
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push(this.removeEmptyValues(eventInfo));
    } catch (e) {
      console.error(e);
    }
  }

  removeEmptyValues(data: Record<string, unknown>) {
    return Object.fromEntries(Object.entries(data).filter(([_, value]) => value !== null));
  }

  trackCustomEvents() {
    if (this.customEventsRegistered) return;

    this.customEventsRegistered = true;
    // Commerce Events
    addTrackingEventListener(TrackingEvents.TrackingOrderSummaryPurchase, (event) => {
      this.trackCommerceEvent("purchase", event.detail?.additionalData);
    });

    addTrackingEventListener(TrackingEvents.TrackingOrderTradeIn, (event) => {
      this.trackCommerceEvent("trade_in", event.detail?.additionalData);
    });

    addTrackingEventListener(TrackingEvents.TrackingCheckoutAddPaymentInfo, (event) => {
      this.trackCommerceEvent("add_payment_info", event.detail?.additionalData);
    });

    addTrackingEventListener(TrackingEvents.TrackingCheckoutAddShippingInfo, (event) => {
      this.trackCommerceEvent("add_shipping_info", event.detail?.additionalData);
    });

    addTrackingEventListener(TrackingEvents.TrackingCheckoutBeginCheckout, (event) => {
      this.trackCommerceEvent("begin_checkout", event.detail?.additionalData);
    });

    addTrackingEventListener(TrackingEvents.TrackingCheckoutProgress, (event) => {
      this.trackCommerceEvent("checkout_progress", event.detail?.additionalData);
    });

    addTrackingEventListener(TrackingEvents.TrackingCartOpen, (event) => {
      this.trackCommerceEvent("view_cart", event.detail?.additionalData);
    });

    addTrackingEventListener(TrackingEvents.TrackingCartQuantityAdd, (event) => {
      this.trackCommerceEvent("add_to_cart", event.detail?.additionalData);
    });

    addTrackingEventListener(TrackingEvents.TrackingCartQuantityRemove, (event) => {
      this.trackCommerceEvent("remove_from_cart", event.detail?.additionalData);
    });

    addTrackingEventListener(TrackingEvents.TrackingViewItemList, (event) => {
      this.trackCommerceEvent("view_item_list", event.detail?.additionalData);
    });

    addTrackingEventListener(TrackingEvents.TrackingSelectItem, (event) => {
      this.trackCommerceEvent("select_item", event.detail?.additionalData);
    });

    addTrackingEventListener(TrackingEvents.TrackingViewItem, (event) => {
      this.trackCommerceEvent("view_item", event.detail?.additionalData);
    });

    addTrackingEventListener(TrackingEvents.TrackingMyOrdersReorder, (event) => {
      this.trackCommerceEvent("re_order", event.detail?.additionalData);
    });

    // My Subscription Events
    addTrackingEventListener(TrackingEvents.TrackingMySubscriptionPause, () => {
      this.trackCommerceEvent("pause_subscription");
    });
    addTrackingEventListener(TrackingEvents.TrackingMySubscriptionResume, () => {
      this.trackCommerceEvent("resume_subscription");
    });
    addTrackingEventListener(TrackingEvents.TrackingMySubscriptionStop, (event) => {
      this.trackCommerceEvent("stop_subscription", event.detail?.additionalData);
    });
    addTrackingEventListener(TrackingEvents.TrackingMySubscriptionAddItem, (event) => {
      this.trackCommerceEvent("add_to_subscription", event.detail?.additionalData);
    });
    addTrackingEventListener(TrackingEvents.TrackingMySubscriptionRemoveItem, (event) => {
      this.trackCommerceEvent("remove_item", event.detail?.additionalData);
    });
    addTrackingEventListener(TrackingEvents.TrackingMySubscriptionIncreaseCarton, (event) => {
      this.trackCommerceEvent("increase_carton", event.detail?.additionalData);
    });
    addTrackingEventListener(TrackingEvents.TrackingMySubscriptionDecreaseCarton, (event) => {
      this.trackCommerceEvent("decrease_carton", event.detail?.additionalData);
    });

    // Events
    addTrackingEventListener(TrackingEvents.TrackingShopFilterInteraction, (event) => {
      this.trackEvent("filter_interaction", event.detail?.additionalData);
    });
    addTrackingEventListener(TrackingEvents.TrackingUserAccountDeviceRegister, (event) => {
      this.trackEvent("device_register", event.detail?.additionalData);
    });

    addTrackingEventListener(TrackingEvents.TrackingUserAccountDeviceDeregister, (event) => {
      this.trackEvent("device_deregister", event.detail?.additionalData);
    });

    addTrackingEventListener(TrackingEvents.TrackingUserLoginDeviceRegister, (event) => {
      this.trackEvent("device_register", event.detail?.additionalData);
    });

    addTrackingEventListener(TrackingEvents.TrackingCtaClick, (event) => {
      this.trackEvent("cta_click", event.detail?.additionalData);
    });

    addTrackingEventListener(TrackingEvents.TrackingCheckoutOrderPaymentCtaClick, (event) => {
      this.trackEvent("cta_click_payment", event.detail?.additionalData);
    });

    addTrackingEventListener(TrackingEvents.TrackingFaqLink, (event) => {
      this.trackEvent("faq_click", event.detail?.additionalData);
    });

    addTrackingEventListener(TrackingEvents.TrackingFaqSearchClick, (event) => {
      this.trackEvent("faq_search", event.detail?.additionalData);
    });

    addTrackingEventListener(TrackingEvents.TrackingFaqSearchResultClick, (event) => {
      this.trackEvent("faq_search_results_click", event.detail?.additionalData);
    });

    addTrackingEventListener(TrackingEvents.TrackingSocialIconClick, (event) => {
      this.trackEvent("share_click", event.detail?.additionalData);
    });

    addTrackingEventListener(TrackingEvents.TrackingReferralCodeCopyButtonClick, (event) => {
      this.trackEvent("copy_a_code", event.detail?.additionalData);
    });

    addTrackingEventListener(TrackingEvents.TrackingReferralCodeInserted, (event) => {
      this.trackEvent("referral_code_inserted", event.detail?.additionalData);
    });

    addTrackingEventListener(TrackingEvents.TrackingPointsHistoryClick, (event) => {
      this.trackEvent("points_history_click", event.detail?.additionalData);
    });

    addTrackingEventListener(TrackingEvents.TrackingCodeSubmitSuccess, (event) => {
      this.trackEvent("pack_code_insertion", event.detail?.additionalData);
    });

    addTrackingEventListener(TrackingEvents.TrackingListClick, (event) => {
      this.trackEvent("list_click", event.detail?.additionalData);
    });

    addTrackingEventListener(TrackingEvents.TrackingLoginClick, (event) => {
      this.trackEvent("login_click", event.detail?.additionalData);
    });

    addTrackingEventListener(TrackingEvents.TrackingLoginSuccess, (event) => {
      this.trackEvent("login", event.detail?.additionalData);
    });

    addTrackingEventListener(TrackingEvents.TrackingLoginFail, (event) => {
      this.trackEvent("login_fail", event.detail?.additionalData);
    });

    addTrackingEventListener(TrackingEvents.TrackingLoginKakao, (event) => {
      this.trackEvent("kakao_login", event.detail?.additionalData);
    });

    addTrackingEventListener(TrackingEvents.TrackingCreateAccountClick, async (event) => {
      await this.trackEvent("create_account_click", event.detail?.additionalData);
    });

    addTrackingEventListener(TrackingEvents.TrackingLogoutSuccess, (event) => {
      this.trackEvent("sign_out", event.detail?.additionalData);
    });

    addTrackingEventListener(TrackingEvents.TrackingEvent, (event) => {
      if (event.detail?.additionalData?.event) {
        this.trackEvent(event.detail?.additionalData.event, event.detail?.additionalData);
      }
    });

    addTrackingEventListener(TrackingEvents.TrackingUnsubscribe, (event) => {
      this.trackEvent("unsubscribe", event.detail?.additionalData);
    });

    addTrackingEventListener(TrackingEvents.TrackingFreeTrialStart, (event) => {
      this.trackEvent("free_trial_start", event.detail?.additionalData);
    });

    addTrackingEventListener(TrackingEvents.TrackingSignUpRegular, (event) => {
      this.trackEvent("sign_up", event.detail?.additionalData);
    });

    addTrackingEventListener(TrackingEvents.TrackingSignUpProgress, (event) => {
      this.trackEvent("sign_up_progress", event.detail?.additionalData);
    });

    addTrackingEventListener(TrackingEvents.TrackingRedeemClick, (event) => {
      this.trackEvent("redeem_click", event.detail?.additionalData);
    });

    addTrackingEventListener(TrackingEvents.TrackingRewardDiscoverMoreClick, (event) => {
      this.trackEvent("reward_discover_more_click", event.detail?.additionalData);
    });

    addTrackingEventListener(TrackingEvents.TrackingRedeemCheckout, (event) => {
      this.trackEvent("redeem_checkout", event.detail?.additionalData);
    });

    addTrackingEventListener(TrackingEvents.TrackingLotteryClick, (event) => {
      this.trackEvent("lottery_click", event.detail?.additionalData);
    });

    addTrackingEventListener(TrackingEvents.TrackingCheckoutPageView, (event) => {
      this.trackEvent("page_view", event.detail?.additionalData);
    });

    addTrackingEventListener(TrackingEvents.TrackingFlavorFinderProgress, (event) => {
      this.trackEvent("flavor_finder_progress", event.detail?.additionalData);
    });

    addTrackingEventListener(TrackingEvents.TrackingFlavorFinderResult, (event) => {
      this.trackEvent("flavor_finder_result", event.detail?.additionalData);
    });

    addTrackingEventListener(TrackingEvents.VideoClick, (event) => {
      this.trackEvent("video_click", event.detail?.additionalData);
    });

    addTrackingEventListener(TrackingEvents.VideoStop, (event) => {
      this.trackEvent("video_stop", event.detail?.additionalData);
    });

    addTrackingEventListener(TrackingEvents.VideoComplete, (event) => {
      this.trackEvent("video_complete", event.detail?.additionalData);
    });

    addTrackingEventListener(TrackingEvents.VideoProgress, (event) => {
      this.trackEvent("video_progress", event.detail?.additionalData);
    });

    addTrackingEventListener(TrackingEvents.TrackingHardAgeVerificationStart, (event) => {
      this.trackEvent("hav_start", event.detail?.additionalData);
    });

    addTrackingEventListener(TrackingEvents.TrackingHardAgeVerificationSuccess, (event) => {
      this.trackEvent("hav_success", event.detail?.additionalData);
    });

    addTrackingEventListener(TrackingEvents.TrackingFollow, (event) => {
      this.trackEvent("follow", event.detail?.additionalData);
    });

    addTrackingEventListener(TrackingEvents.TrackingContactUsClick, (event) => {
      this.trackEvent("contact_click", event.detail?.additionalData);
    });

    addTrackingEventListener(TrackingEvents.TrackingContactUsPhoneClick, (event) => {
      this.trackEvent("phone_click", event.detail?.additionalData);
    });

    addTrackingEventListener(TrackingEvents.TrackingBlogArticleClick, (event) => {
      this.trackEvent("article_click", event.detail?.additionalData);
    });

    addTrackingEventListener(TrackingEvents.TrackingStoreLocatorClick, (event) => {
      this.trackEvent("store_locator_click", event.detail?.additionalData);
    });

    addTrackingEventListener(TrackingEvents.TrackingSubscribeBlockClick, (event) => {
      this.trackEvent("newsletter_subscription", event.detail?.additionalData);
    });

    addTrackingEventListener(TrackingEvents.TrackingMenuNavigationClick, (event) => {
      this.trackEvent("menu_navigation", event.detail?.additionalData);
    });

    addTrackingEventListener(TrackingEvents.TrackingMenuNavigationListClick, (event) => {
      this.trackEvent("list_click", event.detail?.additionalData);
    });

    addTrackingEventListener(TrackingEvents.TrackingCarouselClick, (event) => {
      this.trackEvent("click_carousel", event.detail?.additionalData);
    });

    addTrackingEventListener(TrackingEvents.TrackingAgeVerificationClick, (event) => {
      this.trackEvent("age_verification", event.detail?.additionalData);
    });

    addTrackingEventListener(TrackingEvents.TrackingSurveyStart, (event) => {
      this.trackEvent("survey_start", event.detail?.additionalData);
    });

    addTrackingEventListener(TrackingEvents.TrackingSurveyProgress, (event) => {
      this.trackEvent("survey_progress", event.detail?.additionalData);
    });

    addTrackingEventListener(TrackingEvents.TrackingSurveyCompletion, (event) => {
      this.trackEvent("survey_completion", event.detail?.additionalData);
    });

    addTrackingEventListener(TrackingEvents.TrackingPageView, (event) => {
      this.trackEvent("page_view", event.detail?.additionalData);
    });

    addTrackingEventListener(TrackingEvents.TrackingPopinClose, (event) => {
      this.trackEvent("popin_close", event.detail?.additionalData);
    });

    addTrackingEventListener(TrackingEvents.TrackingPopinLinkClick, (event) => {
      this.trackEvent("popin_link_click", event.detail?.additionalData);
    });

    addTrackingEventListener(TrackingEvents.TrackingPopinView, (event) => {
      this.trackEvent("popin_view", event.detail?.additionalData);
    });

    addTrackingEventListener(TrackingEvents.TrackingQuizStart, (event) => {
      this.trackEvent("quiz_start", event.detail?.additionalData);
    });

    addTrackingEventListener(TrackingEvents.TrackingQuizEnd, (event) => {
      this.trackEvent("quiz_end", event.detail?.additionalData);
    });

    addTrackingEventListener(TrackingEvents.TrackingCrossSellPopupImpression, (event) => {
      this.trackEvent("cross_sell_popup_impression", event.detail?.additionalData);
    });
  }

  static getInstance() {
    if (!instance) {
      instance = new BaseGTMRunner();
    }

    return instance;
  }
}
